import React from 'react';
import Permissions from '../auth/Permissions';
import sortByString, { getIdMap, getId } from '../helpers/helpers';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { CalendarOutlined } from '@ant-design/icons';
import Colors from '../constants/Colors';
import UserRedesignListClock from './UserRedesignListClock';
import TagRender from '../common/TagRender';

export default {};

export const createPhaseMap = (phases, costcodes = []) => {
  const phaseMap = {};
  const costcodeMap = getIdMap(costcodes);
  const unphasedSet = new Set(costcodes.map(getId));

  phases.forEach((phase) => {
    if (!(phase.id in phaseMap)) {
      phaseMap[phase.id] = { ...phase, costcodes: [] };
    }
    const ourCC = phase.costcodeId in costcodeMap ? costcodeMap[phase.costcodeId] : {};
    phaseMap[phase.id].costcodes.push({
      ...ourCC,
      id: phase.costcodeId,
      hours: phase.hours,
      startDate: phase.startDate,
      endDate: phase.endDate,
    });
    unphasedSet.delete(phase.costcodeId);
  });
  const unphasedCC = Array.from(unphasedSet).map((cc) => costcodeMap[cc]);
  return { phaseMap, unphasedCC };
};

export const mergeUsersProjects = ({
  users = [],
  projects = [],
  onUserClock,
  onTimeCardClick,
  firstDayOfRunningPayPeriod,
  roundingInterval,
  roundingSetting,
  roundingType,
  ownerId,
  unions: {
    locals = [],
    classes = [],
  } = {},
}) => {
  const projectMap = getIdMap(projects);

  const localMap = getIdMap(locals);
  const classMap = getIdMap(classes);

  const newUsers = users.map((user) => {
    const userObj = {
      ...user,
      onUserClock,
      onTimeCardClick,
      firstDayOfRunningPayPeriod,
      roundingInterval,
      roundingSetting,
      roundingType,
      isOwner: user.id === ownerId,
      union: {},
    };
    const { classId } = user;

    if (classId) {
      userObj.union.classId = classId;
      const {
        [classId]: {
          localId,
        } = {},
      } = classMap;
      if (localId) {
        userObj.union.localId = localId;
        const {
          [localId]: {
            unionId,
          } = {},
        } = localMap;
        userObj.union.unionId = unionId;
      }
    }
    return userObj;
  });

  return {
    users: newUsers,
    projectMap,
  };
};

export const hasRolePermission = (user = {}, permissionKey = 'USERS') => {
  if (!user.id) return false;
  return Permissions.hasForUser(user.id, user.position, permissionKey);
};

export const hasPermissions = (user = {}) => hasRolePermission(user, 'USERS');

export const canManageUserCertifications = (user = {}) => (
  hasPermissions(user) || hasRolePermission(user, 'MANAGE_USER_CERTIFICATIONS')
);

export const canAddUserCertifications = (user = {}) => (
  canManageUserCertifications(user) || hasRolePermission(user, 'ADD_CERTIFICATIONS_USER')
);

export const canManageVendorCertifications = () => (
  Permissions.has('VENDORS_WRITE') || Permissions.has('MANAGE_VENDOR_CERTIFICATIONS')
);

export const canAddVendorCertifications = () => (
  canManageVendorCertifications() || Permissions.has('ADD_CERTIFICATIONS_VENDOR')
);

export const canManageEquipmentCertifications = () => (
  Permissions.has('EQUIPMENT_WRITE') || Permissions.has('MANAGE_EQUIPMENT_CERTIFICATIONS')
);

export const canAddEquipmentCertifications = () => (
  canManageEquipmentCertifications() || Permissions.has('ADD_CERTIFICATIONS_EQUIPMENT')
);

export const canManageCertifications = (entityType, user = {}) => {
  switch (entityType) {
    case 'users':
      return canManageUserCertifications(user);
    case 'equipment':
      return canManageEquipmentCertifications();
    case 'vendors':
      return canManageVendorCertifications();
    default:
      return false;
  }
};

export const canAddCertifications = (entityType, user = {}) => {
  switch (entityType) {
    case 'users':
      return canAddUserCertifications(user);
    case 'equipment':
      return canAddEquipmentCertifications();
    case 'vendors':
      return canAddVendorCertifications();
    default:
      return false;
  }
};

export const getDisplayColumns = ({
  userDivisions,
  divisions,
  onPayPeriodClick,
  timeEntryUserMap,
  useRedesign,
  onUserClock,
  labels,
  userToLabel,
}) => {
  const cols = [
    {
      title: 'ID',
      dataIndex: 'employeeId',
      width: 50,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByString('employeeId'),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: 100,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByString('name'),
    },
    {
      title: 'Clock',
      dataIndex: 'clock',
      width: 200,
      render: (_, record) => (
        <UserRedesignListClock
          {...record}
          tasks={timeEntryUserMap[record.id] ?? []}
          timeEntryUserMap={timeEntryUserMap}
          onUserClock={onUserClock}

        />
      ),
    },
  ];

  if (Object.keys(divisions).length > 1) {
    cols.push({
      title: 'Divisions',
      dataIndex: 'id',
      width: 175,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (id) => (
        <TagRender
          tagMap={divisions}
          tagIds={userDivisions[id]}
          useRedesign={useRedesign}
        />
      ),
    });
  }
  cols.push(
    {
      title: 'Position',
      dataIndex: 'position',
      width: 100,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByString('position'),
    },
    {
      title: 'Weekly pay period',
      dataIndex: 'weeklyPayPeriod',
      width: 125,
      render: (_, record) => {
        const canViewCards = Permissions.hasForUser(record.id, record.position, 'TIMECARD_VIEW');
        if (!canViewCards) return null;

        return (
          <OnTraccrButton
            style={{
              backgroundColor: '#D6E9FF80',
              color: Colors.BLUE_600,
              border: '1px solid #D6E9FF80',
              borderRadius: 16,
            }}
            title="Weekly pay period"
            iconLeft
            icon={<CalendarOutlined color={Colors.BLUE_600} />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onPayPeriodClick(timeEntryUserMap[record.id] ?? [], record.id);
            }}
          />
        )
      }
    },
  );

  if (labels.length > 0) {
    const labelMap = getIdMap(labels);
    cols.push({
      title: 'Labels',
      dataIndex: 'labels',
      width: 125,
      render: (_, record) => {
        const {
          [record.id]: ourLabels = [],
        } = userToLabel;
        return (
          <TagRender
            tagMap={labelMap}
            tagIds={ourLabels}
            useRedesign={useRedesign}
            labelKey="title"
          />
        );
      },
    });
  }
  return cols;
};

export const hasViewPermission = (targetUser = {}) => {
  if (!targetUser.id) return false;
  return Permissions.hasForUser(targetUser.id, targetUser.position, 'USERS_VIEW');
};
